import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { MessageCircle, Users, Trash2 } from "lucide-react"
import { cn } from "@/lib/utils"

interface Conversation {
  id: string
  title?: string
  conversation_type: string
  participants: Array<{
    profiles: {
      id: string
      full_name: string
      role: string
    } | null
  }>
  last_message?: {
    content: string
    created_at: string
    sender: {
      full_name: string
    }
  }
  unread_count: number
  updated_at: string
}

interface ConversationsListProps {
  conversations: Conversation[]
  onConversationSelect: (conversation: Conversation) => void
  currentUserId?: string
  showBroadcastOption?: boolean
  onBroadcastClick?: () => void
  onDeleteConversation?: (conversationId: string) => void
}

export const ConversationsList = ({
  conversations,
  onConversationSelect,
  currentUserId,
  showBroadcastOption = false,
  onBroadcastClick,
  onDeleteConversation
}: ConversationsListProps) => {
  const getConversationName = (conversation: Conversation) => {
    if (conversation.title && !conversation.title.startsWith('Conversa com ')) {
      return conversation.title
    }
    
    const otherParticipant = conversation.participants.find(
      p => p.profiles && p.profiles.id !== currentUserId
    )
    
    return otherParticipant?.profiles?.full_name || "Conversa"
  }

  const getConversationRole = (conversation: Conversation) => {
    const otherParticipant = conversation.participants.find(
      p => p.profiles && p.profiles.id !== currentUserId
    )
    
    const roleNames = {
      'morador': 'Morador',
      'sindico': 'Síndico',
      'porteiro': 'Portaria',
      'administracao': 'Administração'
    }
    
    return roleNames[otherParticipant?.profiles?.role as keyof typeof roleNames] || 'Usuário'
  }

  const formatLastMessageTime = (date: string) => {
    return format(new Date(date), "HH:mm", { locale: ptBR })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          Mensagens
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {showBroadcastOption && onBroadcastClick && (
          <Button
            variant="ghost"
            className="w-full justify-start p-4 border-b"
            onClick={onBroadcastClick}
          >
            <Users className="h-5 w-5 mr-3" />
            <div className="flex-1 text-left">
              <p className="font-medium">Todos os moradores</p>
              <p className="text-sm text-muted-foreground">
                Enviar comunicado coletivo
              </p>
            </div>
          </Button>
        )}
        
        {conversations.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Nenhuma conversa ainda</p>
          </div>
        ) : (
          <div className="divide-y">
            {conversations.map((conversation) => (
              <div key={conversation.id} className="flex items-center group">
                <Button
                  variant="ghost"
                  className="flex-1 justify-start p-4 h-auto"
                  onClick={() => onConversationSelect(conversation)}
                >
                  <Avatar className="h-10 w-10 mr-3">
                    <AvatarFallback>
                      {getConversationName(conversation).charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 text-left">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium truncate">
                        {getConversationName(conversation)}
                      </p>
                      {conversation.unread_count > 0 && (
                        <Badge variant="destructive" className="ml-2">
                          {conversation.unread_count}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">
                        {getConversationRole(conversation)}
                      </p>
                      {conversation.last_message && (
                        <span className="text-xs text-muted-foreground">
                          {formatLastMessageTime(conversation.last_message.created_at)}
                        </span>
                      )}
                    </div>
                    
                    {conversation.last_message && (
                      <p className="text-sm text-muted-foreground truncate mt-1">
                        {conversation.last_message.content}
                      </p>
                    )}
                  </div>
                </Button>
                
                {onDeleteConversation && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-2 mr-2"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteConversation(conversation.id)
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}